import { CurrencySnapshot } from "./types";

// Hold (HODL) invoices, as serialized by BobHoldInvoice.front_end_row.
// zz-support-hold-invoices-fable.txt 8.7, 10.

export type HoldState = "open" | "accepted" | "settled" | "canceled" | "expired";

export type HoldCancelReason =
  | "user"
  | "nwc_client"
  | "decision_timeout"
  | "policy_cap"
  | "lnd_auto_cancel"
  | "unknown";

export interface HoldInvoice {
  uuid: string;
  hold_state: HoldState;
  amount_in_satoshis: number;
  accepted_amount_in_satoshis: number | null;
  pay_request: string | null;
  payment_hash: string;
  our_provided_description: string | null;
  created_at: number;
  expires_at: number | null;
  confirmed_at: number | null;
  hold_accepted_at: number | null;
  hold_decision_deadline_at: number | null;
  hold_canceled_at: number | null;
  hold_min_htlc_timeout_height: number | null;
  blocks_remaining: number | null;
  htlc_count: number;
  can_settle: boolean;
  can_cancel: boolean;
  cancel_pending: boolean;
  settle_pending: boolean;
  origin: "web" | "nwc";
  origin_name: string | null;
  hold_cancel_reason: HoldCancelReason | null;
  currency_snapshot: CurrencySnapshot | null;
}

// every number the UI prints comes from the server (D2)
export interface HoldInvoiceSettings {
  max_in_flight_per_account: number;
  max_accepted_per_account: number;
  decision_window_seconds: number;
  min_amount_sats: number;
  max_amount_sats: number;
  min_expiry_seconds: number;
  max_expiry_seconds: number;
}

// accepted and nobody has decided yet: the only ones that nag. A settle or
// cancel that is recorded but not yet confirmed by lnd is decided.
export const isUndecidedHoldInvoice = (hold: HoldInvoice) =>
  hold.hold_state === "accepted" && !hold.settle_pending && !hold.cancel_pending;

export const holdAmountSatoshis = (hold: HoldInvoice) =>
  hold.accepted_amount_in_satoshis ?? hold.amount_in_satoshis;

export const formatDurationSeconds = (seconds: number) => {
  if (seconds >= 3600 && seconds % 3600 === 0) {
    const hours = seconds / 3600;
    return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  }
  if (seconds >= 3600) return `${(seconds / 3600).toFixed(1)} hours`;
  const minutes = Math.round(seconds / 60);
  return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
};

const NUMBER_WORDS = [
  "zero", "one", "two", "three", "four", "five", "six", "seven", "eight",
  "nine", "ten", "eleven", "twelve",
];

// "two (2)"; plain digits past twelve
export const numberInWordsAndDigits = (n: number) =>
  Number.isInteger(n) && n >= 0 && n < NUMBER_WORDS.length
    ? `${NUMBER_WORDS[n]} (${n})`
    : `${n}`;

// "two (2) hours", "one (1) hour", "90 minutes": whole hours in
// words, otherwise minutes (words only up to twelve)
export const formatDurationInWords = (seconds: number) => {
  if (seconds >= 3600 && seconds % 3600 === 0) {
    const hours = seconds / 3600;
    return `${numberInWordsAndDigits(hours)} ${hours === 1 ? "hour" : "hours"}`;
  }
  const minutes = Math.round(seconds / 60);
  return `${numberInWordsAndDigits(minutes)} ${minutes === 1 ? "minute" : "minutes"}`;
};

// "1:02:03", "47:12", "0:00"
export const formatCountdown = (msLeft: number) => {
  const total = Math.max(0, Math.floor(msLeft / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`;
};

export const minutesLeftLabel = (msLeft: number) => {
  const minutes = Math.max(0, Math.ceil(msLeft / 60_000));
  if (minutes >= 120) return `${Math.floor(minutes / 60)} h left`;
  return `${minutes} min left`;
};

// the Recent section's badge
export const holdOutcome = (
  hold: HoldInvoice,
): { label: string; color: string } => {
  if (hold.hold_state === "settled") return { label: "Accepted", color: "green" };
  if (hold.hold_state === "expired")
    return { label: "Payer never paid", color: "gray" };
  if (hold.hold_state === "canceled") {
    switch (hold.hold_cancel_reason) {
      case "user":
        return { label: "You rejected", color: "gray" };
      case "nwc_client":
        return { label: "Rejected via NWC", color: "gray" };
      case "decision_timeout":
        return { label: "Timed out - refunded to payer", color: "orange" };
      case "policy_cap":
        return { label: "Refused: too many pending", color: "orange" };
      case "lnd_auto_cancel":
        return { label: "Network deadline passed - refunded", color: "orange" };
      case "unknown":
        return { label: "Canceled by Rizful support", color: "gray" };
      default:
        return { label: "Canceled", color: "gray" };
    }
  }
  if (hold.hold_state === "accepted") return { label: "Waiting", color: "red" };
  return { label: "Open", color: "blue" };
};
